import React, { useState, useMemo, useEffect } from 'react';
import BWAccordion from 'components/shared/BWAccordion';
import FormInput from 'components/shared/BWFormControl/FormInput';
import DataTable from 'components/shared/DataTable';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { COUPON_PERMISSION } from 'pages/Coupon/utils/constants';
import { showConfirmModal } from 'actions/global';
import { useDispatch } from 'react-redux';
import { defineCustomerType } from 'pages/CustomerType/components/contain';
import CouponCustomerTypeModal from './modal/CouponCustomerTypeModal';
import ICON_COMMON from 'utils/icons.common';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { PortalIcon } from 'utils/baochauTheme';

const CustomerScopeWrapper = styled.div`
  .bw_customer_scope_grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .bw_customer_scope_card {
    position: relative;
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr) 20px;
    gap: 12px;
    align-items: center;
    min-height: 82px;
    padding: 14px 16px;
    border: 1px solid #d6dee9;
    border-radius: 12px;
    background: #fff;
    cursor: pointer;
    transition:
      border-color 0.18s ease,
      background-color 0.18s ease,
      box-shadow 0.18s ease,
      transform 0.18s ease;
  }

  .bw_customer_scope_card:hover:not(.is-disabled) {
    border-color: #f2aaa5;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);
  }

  .bw_customer_scope_card > input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .bw_customer_scope_icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #f2f4f7;
    color: #667085;
    font-size: 18px;
  }

  .bw_customer_scope_title {
    display: block;
    margin-bottom: 3px;
    color: #1d2939;
    font-size: 14px;
    font-weight: 700;
  }

  .bw_customer_scope_description {
    display: block;
    color: #667085;
    font-size: 12.5px;
    line-height: 18px;
  }

  .bw_customer_scope_indicator {
    width: 20px;
    height: 20px;
    border: 2px solid #c7d0dd;
    border-radius: 50%;
    background: #fff;
  }

  .bw_customer_scope_card.is-active {
    border-color: #ed3237;
    background: linear-gradient(180deg, #fffafa 0%, #fff5f5 100%);
    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.08);
  }

  .bw_customer_scope_card.is-active .bw_customer_scope_icon {
    background: #fee4e2;
    color: #d92d32;
  }

  .bw_customer_scope_card.is-active .bw_customer_scope_indicator {
    border-color: #ed3237;
    background: #ed3237;
    box-shadow: inset 0 0 0 4px #fff;
  }

  .bw_customer_scope_card.is-disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  .bw_customer_selection_panel {
    margin-top: 12px;
    padding: 14px;
    border: 1px solid #e4e7ec;
    border-radius: 12px;
    background: #fcfcfd;
  }

  .bw_customer_selection_summary {
    margin-bottom: 8px;
    color: #475467;
    font-size: 13px;
  }

  .bw_customer_selection_summary strong {
    color: #101828;
  }

  @media (max-width: 900px) {
    .bw_customer_scope_grid {
      grid-template-columns: 1fr;
    }
  }
`;

const CouponCustomerType = ({ loading, title, disabled }) => {
  const methods = useFormContext();
  const { clearErrors, register, unregister, watch } = methods;
  const isAllCustomerType = watch('is_all_customer_type');
  const customerTypeList = watch('customer_type_list');
  const hasSelectedCustomerTypes = customerTypeList?.length > 0;
  const dispatch = useDispatch();
  const [modalCustomerType, setModalCustomerType] = useState(undefined);

  const handleChangeCustomerScope = (applyAll) => {
    methods.setValue('is_all_customer_type', applyAll ? 1 : 0, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    if (applyAll) {
      methods.setValue('customer_type_list', []);
      methods.clearErrors('check_customer_type');
    }
  };

  useEffect(() => {
    clearErrors('check_customer_type');
    if (isAllCustomerType || hasSelectedCustomerTypes) {
      unregister('check_customer_type', {});
    } else {
      register('check_customer_type', { required: 'Hạng khách áp dụng là bắt buộc' });
    }
  }, [clearErrors, hasSelectedCustomerTypes, isAllCustomerType, register, unregister]);

  const columns = useMemo(
    () => [
      {
        header: 'Tên hạng khách hàng',
        accessor: 'customer_type_name',
        classNameHeader: ' bw_text_center',
      },
      {
        header: 'Loại',
        accessor: 'type_customer',
        classNameHeader: ' bw_text_center',
        classNameBody: ' bw_text_center',
        formatter: (p) => (
          <span className='bw_label_outline bw_label_outline_success text-center'>
            {defineCustomerType[p?.type_customer]?.label}
          </span>
        ),
      },
      // {
      //   header: 'Công ty áp dụng',
      //   accessor: 'company_name',
      // },
      {
        header: 'Miền áp dụng',
        accessor: 'business_name',
        classNameHeader: ' bw_text_center',
        formatter: (p) => (String(p?.business_id) === '-1' ? 'Tất cả' : p?.business_name),
      },
    ],
    [],
  );

  const actions = useMemo(() => {
    return [
      {
        globalAction: true,
        icon: ICON_COMMON.add,
        type: 'success',
        content: 'Chọn hạng khách hàng',
        permission: COUPON_PERMISSION.SELECT_CUSTOMER_TYPE,
        hidden: disabled,
        showContent: true,
        onClick: () => {
          setModalCustomerType(true);
        },
      },
      {
        icon: ICON_COMMON.trash,
        color: 'red',
        hidden: disabled,
        permission: COUPON_PERMISSION.SELECT_CUSTOMER_TYPE,
        onClick: (value) => {
          dispatch(
            showConfirmModal([`Xoá ${value?.customer_type_name} ra khỏi danh sách khách hàng áp dụng ?`], () => {
              const customer_type_list = _.cloneDeep(methods.watch('customer_type_list'));
              methods.setValue(
                'customer_type_list',
                customer_type_list.filter((o) => parseInt(o?.customer_type_id) !== parseInt(value?.customer_type_id)),
              );
              return;
            }),
          );
        },
      },
    ];
  }, [disabled, dispatch, methods]);

  return (
    <React.Fragment>
      <BWAccordion title={title}>
        <CustomerScopeWrapper className='bw_frm_box'>
          <FormInput hidden type='hidden' field='is_all_customer_type' />
          <div className='bw_customer_scope_grid'>
            <label
              className={`bw_customer_scope_card ${Boolean(isAllCustomerType) ? 'is-active' : ''} ${
                disabled ? 'is-disabled' : ''
              }`}>
              <input
                type='radio'
                name='bw_customer_apply_scope'
                checked={Boolean(isAllCustomerType)}
                disabled={disabled}
                onChange={() => handleChangeCustomerScope(true)}
              />
              <span className='bw_customer_scope_icon'>
                <PortalIcon icon='fi fi-rr-users-alt' />
              </span>
              <span>
                <span className='bw_customer_scope_title'>Tất cả hạng khách hàng</span>
                <span className='bw_customer_scope_description'>Mọi khách hàng đủ điều kiện đều có thể sử dụng.</span>
              </span>
              <span className='bw_customer_scope_indicator' />
            </label>
            <label
              className={`bw_customer_scope_card ${!Boolean(isAllCustomerType) ? 'is-active' : ''} ${
                disabled ? 'is-disabled' : ''
              }`}>
              <input
                type='radio'
                name='bw_customer_apply_scope'
                checked={!Boolean(isAllCustomerType)}
                disabled={disabled}
                onChange={() => handleChangeCustomerScope(false)}
              />
              <span className='bw_customer_scope_icon'>
                <PortalIcon icon='fi fi-rr-user-add' />
              </span>
              <span>
                <span className='bw_customer_scope_title'>Hạng khách hàng cụ thể</span>
                <span className='bw_customer_scope_description'>Chỉ áp dụng cho các hạng khách được lựa chọn.</span>
              </span>
              <span className='bw_customer_scope_indicator' />
            </label>
          </div>
          {!Boolean(isAllCustomerType) && (
            <div className='bw_customer_selection_panel'>
              <div className='bw_customer_selection_summary'>
                Đã chọn <strong>{customerTypeList?.length || 0}</strong> hạng khách hàng
              </div>
              <DataTable
                hiddenActionRow
                noPaging
                noSelect
                loading={loading}
                data={methods.watch('customer_type_list')}
                columns={columns}
                actions={actions}
              />
              <FormInput hidden type='text' field='check_customer_type' />
            </div>
          )}
        </CustomerScopeWrapper>
      </BWAccordion>
      {modalCustomerType && (
        <CouponCustomerTypeModal
          open={modalCustomerType}
          onClose={() => {
            methods.unregister('keyword');
            setModalCustomerType(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

CouponCustomerType.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default CouponCustomerType;
